import React, { useState, useEffect } from 'react';
import {
    Shield,
    AlertCircle,
    ExternalLink,
    CheckCircle,
    Eye,
    EyeOff,
    Key,
    HelpCircle,
    Wallet,
    Info,
    Webhook,
    Loader2
} from 'lucide-react';
import { toast } from 'react-hot-toast';

import { Card, CardHeader, CardBody, Button, TailwindDialog, DialogContent, DialogActions } from '@/components/ui';
import { OrgaConfiguration } from '@/models/orgaConfiguration';
import { savePayPalSecrets, getPayPalSecretsMetadata, validatePayPalSecrets, validateSavedPayPalSecrets, PayPalSecretsMetadata, PayPalValidationResult } from '@/services/payment/paypal-secrets-service';
import PayPalKeyValidationStatus from './PayPalKeyValidationStatus';
import { useUserData } from '@/shared/hooks/useUserData';

interface PayPalApiKeyConfigProps {
    organizationId: string;
    config?: OrgaConfiguration['paypalConfig'];
    onRefresh?: () => void;
}

const PayPalApiKeyConfig: React.FC<PayPalApiKeyConfigProps> = ({
    organizationId,
    config,
    onRefresh
}) => {
    const [loading, setLoading] = useState(false);
    const [showForm, setShowForm] = useState(!config?.hasClientSecret);
    const [metadata, setMetadata] = useState<PayPalSecretsMetadata | null>(null);

    // Form state
    const [clientId, setClientId] = useState(config?.clientId || '');
    const [clientSecret, setClientSecret] = useState('');
    const [isTestMode, setIsTestMode] = useState(config?.isTestMode || false);
    const [showSecret, setShowSecret] = useState(false);
    const [isTesting, setIsTesting] = useState(false);
    const [validationResult, setValidationResult] = useState<PayPalValidationResult | null>(null);
    const [showResultModal, setShowResultModal] = useState(false);
    const [showHelpModal, setShowHelpModal] = useState(false);
    const [modalMode, setModalMode] = useState<'testing' | 'result' | 'saving'>('testing');
    const [currentStep, setCurrentStep] = useState<'idle' | 'validating' | 'saving' | 'success' | 'error'>('idle');
    const { isSuperAdmin } = useUserData();

    // Load metadata on mount
    useEffect(() => {
        if (organizationId) {
            loadMetadata();
        }
    }, [organizationId]);

    const loadMetadata = async () => {
        try {
            const data = await getPayPalSecretsMetadata(organizationId);
            setMetadata(data);
            if (data.hasClientSecret) {
                setShowForm(false);
            }
            setIsTestMode(data.isTestMode);
            // Pre-fill clientId if available
            if (data.clientId) {
                setClientId(data.clientId);
            }
        } catch (error) {
            console.warn('⚠️ [PayPalConfig] Could not load metadata');
        }
    };

    const handleRunTests = async (showModal = true) => {
        // Si on est en mode formulaire (nouveaux identifiants)
        if (showForm) {
            if (!clientId.trim() || !clientSecret.trim()) {
                toast.error('Veuillez entrer vos identifiants PayPal');
                return null;
            }

            setIsTesting(true);
            setValidationResult(null);
            if (showModal) {
                setModalMode('testing');
                setShowResultModal(true);
            }

            try {
                const result = await validatePayPalSecrets({
                    organizationId,
                    clientId: clientId.trim(),
                    clientSecret: clientSecret.trim(),
                    isTestMode
                });

                setValidationResult(result);
                if (showModal) setModalMode('result');

                if (result.isValid && result.scopes.dangerous.length === 0) {
                    setCurrentStep('success');
                    if (showModal) toast.success('✅ Identifiants valides !');
                } else if (!result.isValid) {
                    setCurrentStep('error');
                    if (showModal) toast.error('❌ Identifiants invalides ou permissions manquantes');
                } else if (result.scopes.dangerous.length > 0) {
                    setCurrentStep('error');
                    if (showModal) toast('⚠️ Permissions trop larges détectées', { icon: '⚠️' });
                }

                return result;
            } catch (error) {
                console.error('Validation error:', error);
                if (showModal) {
                    toast.error('Erreur lors de la validation');
                    setModalMode('result');
                }
                return null;
            } finally {
                setIsTesting(false);
            }
        } else {
            // Si on teste les identifiants déjà sauvegardés
            setIsTesting(true);
            setValidationResult(null);
            if (showModal) {
                setModalMode('testing');
                setShowResultModal(true);
            }

            try {
                const result = await validateSavedPayPalSecrets(organizationId);
                setValidationResult(result);

                if (showModal) setModalMode('result');

                if (result.isValid && result.scopes.dangerous.length === 0) {
                    if (showModal) toast.success('✅ Identifiants sauvegardés valides !');
                } else if (!result.isValid) {
                    if (showModal) toast.error('❌ Identifiants sauvegardés invalides');
                }

                return result;
            } catch (error) {
                console.error('Saved validation error:', error);
                if (showModal) {
                    toast.error('Erreur lors de la validation des clés sauvegardées');
                    setModalMode('result');
                }
                return null;
            } finally {
                setIsTesting(false);
            }
        }
    };

    const handleSave = async (e?: React.FormEvent) => {
        if (e) e.preventDefault();

        if (!clientId.trim()) {
            toast.error('Client ID est requis');
            return;
        }

        if (!clientSecret.trim()) {
            toast.error('Client Secret est requis');
            return;
        }

        setLoading(true);
        setModalMode('saving');
        setCurrentStep('validating');
        setShowResultModal(true);
        try {
            // Validation forcée avant sauvegarde
            const validation = await handleRunTests(false);

            if (!validation || !validation.isValid) {
                setCurrentStep('error');
                toast.error('❌ Impossible d\'enregistrer : les identifiants sont invalides ou incomplets.');
                setValidationResult(validation);
                setModalMode('result');
                setLoading(false);
                return;
            }

            if (validation.scopes.dangerous.length > 0) {
                // On prévient mais on ne bloque plus l'enregistrement si c'est valide
                toast('⚠️ Sécurité : Votre application a des permissions étendues (Vault/Disputes).', { icon: '⚠️' });
            }

            setCurrentStep('saving');
            await savePayPalSecrets({
                organizationId,
                clientId: clientId.trim(),
                clientSecret: clientSecret.trim(),
                isTestMode
            });

            setCurrentStep('success');
            toast.success('Configuration PayPal enregistrée avec succès');

            // On ferme la modale après un court délai en cas de succès direct
            setTimeout(() => {
                setShowResultModal(false);
                setClientId('');
                setClientSecret('');
                setShowForm(false);
            }, 1500);

            if (onRefresh) onRefresh();
            loadMetadata();
        } catch (error: any) {
            setCurrentStep('error');
            setModalMode('result');
            toast.error(error.message || 'Erreur lors de l\'enregistrement');
        } finally {
            setLoading(false);
        }
    };

    const isConfigured = metadata?.hasClientSecret || config?.hasClientSecret;
    const hasWebhookConfigured = metadata?.hasWebhookId;

    return (
        <div className="space-y-6">
            <Card variant="default">
                <CardHeader>
                    <div className="flex items-center gap-3">
                        <Wallet className="w-5 h-5 text-primary" />
                        <h2 className="text-lg font-semibold text-foreground">Configuration PayPal</h2>
                    </div>
                </CardHeader>
                <CardBody className="p-5">
                    <p className="text-sm text-foreground-secondary">
                        Configurez les paramètres pour ce mode d'encaissement.
                        <strong> Commissions facturées mensuellement</strong>,
                        <strong> frais de port à gérer manuellement</strong>.
                        Virement immédiat sur votre solde PayPal.
                    </p>
                    {isConfigured && (
                        <div className="flex items-center gap-2 mt-4">
                            <span className="flex items-center gap-1.5 px-2.5 py-1 bg-success/10 text-success text-xs font-medium rounded-full">
                                <CheckCircle className="w-3.5 h-3.5 flex-shrink-0" />
                                <span className="leading-none">Configuré</span>
                            </span>
                            {hasWebhookConfigured && (
                                <span className="flex items-center gap-1.5 px-2.5 py-1 bg-info/10 text-info text-xs font-medium rounded-full">
                                    <Webhook className="w-3.5 h-3.5 flex-shrink-0" />
                                    <span className="leading-none">Webhook actif</span>
                                </span>
                            )}
                            {isTestMode && (
                                <span className="flex items-center gap-1.5 px-2.5 py-1 bg-warning/10 text-warning text-xs font-medium rounded-full">
                                    <AlertCircle className="w-3.5 h-3.5 flex-shrink-0" />
                                    <span className="leading-none">Mode Sandbox</span>
                                </span>
                            )}
                        </div>
                    )}
                </CardBody>
            </Card>

            <Card variant="default">
                <CardBody className="p-5 space-y-5">
                    {showForm || !isConfigured ? (
                        <>
                            {isConfigured && (
                                <div className="flex items-center justify-between mb-4 pb-2 border-b border-border/50">
                                    <h3 className="text-sm font-medium text-foreground">Modification des identifiants</h3>
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => setShowForm(false)}
                                        className="text-foreground-secondary h-8"
                                    >
                                        Annuler
                                    </Button>
                                </div>
                            )}

                            <div className="space-y-4">
                                <div className="space-y-2">
                                    <div className="flex justify-between items-center">
                                        <label className="text-sm font-medium text-foreground">
                                            Client ID
                                        </label>
                                        <button
                                            type="button"
                                            onClick={() => setShowHelpModal(true)}
                                            className="text-xs text-primary hover:text-primary-dark transition-colors flex items-center gap-1"
                                        >
                                            <Info className="w-3.5 h-3.5 flex-shrink-0" />
                                            <span className="leading-none">Comment obtenir mes identifiants ?</span>
                                        </button>
                                    </div>
                                    <input
                                        type="text"
                                        value={clientId}
                                        onChange={(e) => setClientId(e.target.value)}
                                        placeholder="AY..."
                                        className="w-full px-4 py-3 rounded-xl border border-border bg-background text-foreground placeholder:text-foreground-tertiary focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all font-mono text-sm"
                                    />
                                </div>

                                <div className="space-y-2">
                                    <label className="text-sm font-medium text-foreground">
                                        Client Secret
                                    </label>
                                    <div className="relative">
                                        <input
                                            type={showSecret ? 'text' : 'password'}
                                            value={clientSecret}
                                            onChange={(e) => setClientSecret(e.target.value)}
                                            placeholder="EA..."
                                            className="w-full px-4 py-3 rounded-xl border border-border bg-background text-foreground placeholder:text-foreground-tertiary focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all font-mono text-sm pr-12"
                                        />
                                        <button
                                            type="button"
                                            onClick={() => setShowSecret(!showSecret)}
                                            className="absolute right-3 top-1/2 -translate-y-1/2 text-foreground-secondary hover:text-foreground p-1"
                                        >
                                            {showSecret ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                                        </button>
                                    </div>
                                </div>
                            </div>

                            {/* Mode Sandbox - Visible uniquement pour les super admins */}
                            {isSuperAdmin && (
                                <div className="flex items-center gap-3 p-3 bg-background-tertiary rounded-xl border border-border/50">
                                    <input
                                        type="checkbox"
                                        id="isTestMode"
                                        checked={isTestMode}
                                        onChange={(e) => setIsTestMode(e.target.checked)}
                                        className="w-4 h-4 text-primary border-border rounded focus:ring-primary"
                                    />
                                    <label htmlFor="isTestMode" className="text-sm font-medium text-foreground cursor-pointer flex-1">
                                        Utiliser le mode Sandbox (Test)
                                    </label>
                                    <div className="text-warning group relative">
                                        <HelpCircle className="w-4 h-4 cursor-help" />
                                        <div className="absolute bottom-full right-0 mb-2 w-64 p-3 bg-surface border border-border rounded-xl shadow-xl text-xs text-foreground opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-50">
                                            Activez ceci uniquement si vous utilisez des identifiants provenant du dashboard PayPal Sandbox.
                                        </div>
                                    </div>
                                </div>
                            )}

                            {hasWebhookConfigured && (
                                <div className="flex items-start gap-3 p-3 bg-success/5 rounded-lg border border-success/10">
                                    <CheckCircle className="w-5 h-5 text-success flex-shrink-0" />
                                    <div className="text-xs">
                                        <p className="font-medium text-success">Synchronisation active</p>
                                        <p className="text-foreground-secondary mt-0.5">
                                            Le webhook est configuré et opérationnel pour cet environnement.
                                        </p>
                                    </div>
                                </div>
                            )}

                            <div className="flex flex-col sm:flex-row gap-3">
                                <Button
                                    onClick={() => handleRunTests(true)}
                                    variant="outline"
                                    isLoading={isTesting}
                                    disabled={loading || !clientId.trim() || !clientSecret.trim()}
                                    className="flex-1"
                                >
                                    Tester les identifiants
                                </Button>

                                <Button
                                    onClick={handleSave}
                                    variant="primary"
                                    isLoading={loading}
                                    disabled={isTesting || !clientId.trim() || !clientSecret.trim()}
                                    leftIcon={!loading && <Shield className="w-5 h-5" />}
                                    className="flex-1"
                                >
                                    Enregistrer la configuration
                                </Button>
                            </div>
                        </>
                    ) : (
                        <div className="space-y-4">
                            <div className="flex items-center justify-between">
                                <h3 className="text-sm font-medium text-foreground-secondary">Identifiants actifs</h3>
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => {
                                        setClientId(metadata?.clientId || config?.clientId || '');
                                        setIsTestMode(metadata?.isTestMode || config?.isTestMode || false);
                                        setShowForm(true);
                                    }}
                                    leftIcon={<Key className="w-4 h-4" />}
                                    className="h-8 py-0"
                                >
                                    Modifier les clés
                                </Button>
                            </div>
                            <div className="space-y-4">
                                <div className="p-3 bg-background-tertiary rounded-xl border border-border/50">
                                    <p className="text-[10px] uppercase tracking-wider font-bold text-foreground-tertiary mb-1">Client ID</p>
                                    <p className="text-sm font-mono text-foreground truncate">{metadata?.clientId || config?.clientId}</p>
                                </div>
                                <div className="p-3 bg-background-tertiary rounded-xl border border-border/50">
                                    <p className="text-[10px] uppercase tracking-wider font-bold text-foreground-tertiary mb-1">Client Secret</p>
                                    <p className="text-sm font-mono text-foreground truncate">{metadata?.clientSecretMasked || config?.clientSecretMasked}</p>
                                </div>
                            </div>

                            <div className="flex flex-wrap gap-6 pt-2">
                                <div className="flex items-center gap-3">
                                    <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${hasWebhookConfigured ? 'bg-success/10 text-success' : 'bg-warning/10 text-warning'}`}>
                                        {hasWebhookConfigured ? <CheckCircle className="w-4 h-4" /> : <AlertCircle className="w-4 h-4" />}
                                    </div>
                                    <div>
                                        <p className="text-sm font-medium text-foreground">Synchronisation</p>
                                        <p className="text-xs text-foreground-secondary">
                                            {hasWebhookConfigured ? (
                                                <>
                                                    Le webhook est opérationnel
                                                    {config?.lastWebhookReceivedAt && (
                                                        <span className="block mt-1 font-medium text-primary">
                                                            Dernière activité : {new Date(config.lastWebhookReceivedAt).toLocaleString('fr-FR')}
                                                        </span>
                                                    )}
                                                </>
                                            ) : 'Webhook non détecté'}
                                        </p>
                                    </div>
                                </div>
                                <div className="flex items-center gap-3">
                                    <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${isTestMode ? 'bg-warning/10 text-warning' : 'bg-info/10 text-info'}`}>
                                        <Info className="w-4 h-4" />
                                    </div>
                                    <div>
                                        <p className="text-sm font-medium text-foreground">Environnement</p>
                                        <p className="text-xs text-foreground-secondary">
                                            {isTestMode ? 'Mode Sandbox (Test)' : 'Mode Live (Production)'}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    <div className="flex items-start gap-3 p-3 bg-success/5 rounded-lg border border-success/10">
                        <Shield className="w-5 h-5 text-success flex-shrink-0" />
                        <div className="text-xs">
                            <p className="font-medium text-success">Données protégées</p>
                            <p className="text-foreground-secondary mt-0.5">
                                Vos clés sont stockées de manière cryptée dans Google Cloud Secret Manager.
                            </p>
                        </div>
                    </div>

                    {isConfigured && !showForm && (
                        <div className="pt-2">
                            <Button
                                onClick={() => handleRunTests(true)}
                                variant="outline"
                                isLoading={isTesting}
                                className="w-full"
                            >
                                Tester les clés
                            </Button>
                        </div>
                    )}
                </CardBody>
            </Card>

            <TailwindDialog
                isOpen={showHelpModal}
                onClose={() => setShowHelpModal(false)}
                title="Comment obtenir vos identifiants PayPal"
                maxWidth="2xl"
                variant="themed"
                showCloseButton={true}
            >
                <DialogContent className="p-6 space-y-6">
                    <div className="space-y-6">
                        {[
                            {
                                step: 1,
                                title: "Connectez-vous au Dashboard PayPal Developer",
                                desc: "Rendez-vous sur developer.paypal.com",
                                link: "https://developer.paypal.com/dashboard/applications"
                            },
                            {
                                step: 2,
                                title: "Créez une Application \"REST API\"",
                                desc: "Cliquez sur \"Apps & Credentials\", créez une App appelée \"CashLive\"."
                            },
                            {
                                step: 3,
                                title: "Copiez votre \"Client ID\" et \"Secret\"",
                                desc: "Vérifiez que vous êtes en mode \"Live\" pour les vrais paiements."
                            }
                        ].map((s) => (
                            <div key={s.step} className="flex gap-4">
                                <div className="w-8 h-8 rounded-full bg-primary/10 text-primary flex items-center justify-center text-sm font-bold flex-shrink-0">
                                    {s.step}
                                </div>
                                <div className="pt-0.5">
                                    <p className="text-base font-medium text-foreground">{s.title}</p>
                                    <p className="text-sm text-foreground-secondary mt-1">
                                        {s.desc}
                                        {s.link && (
                                            <a href={s.link} target="_blank" rel="noopener noreferrer" className="ml-2 text-primary hover:underline inline-flex items-center gap-0.5">
                                                <ExternalLink className="w-3.5 h-3.5" />
                                                PayPal Developer Dashboard
                                            </a>
                                        )}
                                    </p>
                                </div>
                            </div>
                        ))}
                    </div>

                    <div className="bg-info/5 border border-info/10 rounded-xl p-4 mt-6">
                        <p className="text-sm font-semibold text-info mb-2 flex items-center gap-2">
                            <Info className="w-4 h-4" />
                            Note importante :
                        </p>
                        <p className="text-xs text-foreground-secondary leading-relaxed">
                            Assurez-vous de bien copier les identifiants correspondant au mode choisi (Sandbox pour les tests, Live pour les paiements réels). Les webhooks seront configurés automatiquement par CashLive.
                        </p>
                    </div>
                </DialogContent>
                <DialogActions className="p-4 border-t border-border bg-background-secondary">
                    <Button onClick={() => setShowHelpModal(false)} variant="primary" className="w-full sm:w-auto">
                        J'ai compris
                    </Button>
                </DialogActions>
            </TailwindDialog>

            <TailwindDialog
                isOpen={showResultModal}
                onClose={() => setShowResultModal(false)}
                title={
                    modalMode === 'testing' ? 'Batterie de tests en cours...' :
                        modalMode === 'saving' ? 'Configuration en cours...' :
                            (validationResult?.isValid ?
                                (currentStep === 'success' ? '✅ Configuration réussie' : '✅ Validation réussie') :
                                '❌ Attention requise')
                }
                maxWidth="2xl"
                variant="themed"
                showCloseButton={modalMode === 'result'}
            >
                <div className="overflow-y-auto max-h-[70vh]">
                    <DialogContent className={modalMode === 'result' ? 'p-0' : 'p-6 space-y-4'}>
                        {(modalMode === 'testing' || modalMode === 'saving') && (
                            <div className="space-y-6">
                                <div className="flex items-center justify-center">
                                    <div className="w-16 h-16 rounded-full bg-primary/20 flex items-center justify-center">
                                        <Loader2 className="w-8 h-8 text-primary animate-spin" />
                                    </div>
                                </div>

                                <div className="text-center">
                                    <p className="text-lg font-medium text-foreground">
                                        {currentStep === 'validating' ? 'Validation des identifiants...' :
                                            currentStep === 'saving' ? 'Sauvegarde sécurisée...' :
                                                currentStep === 'success' ? 'Configuration réussie !' :
                                                    'Traitement en cours...'}
                                    </p>
                                    <p className="text-sm text-foreground-secondary mt-2">
                                        Veuillez patienter...
                                    </p>
                                </div>

                                {modalMode === 'saving' && (
                                    <div className="space-y-2">
                                        <div className="flex gap-2">
                                            <div className={`flex-1 h-2 rounded-full transition-all ${['validating', 'saving', 'success', 'error'].includes(currentStep) ? 'bg-primary animate-pulse' : 'bg-background-tertiary'}`} />
                                            <div className={`flex-1 h-2 rounded-full transition-all ${['saving', 'success'].includes(currentStep) ? 'bg-primary animate-pulse' : 'bg-background-tertiary'}`} style={{ animationDelay: '0.2s' }} />
                                            <div className={`flex-1 h-2 rounded-full transition-all ${['success'].includes(currentStep) ? 'bg-primary animate-pulse' : 'bg-background-tertiary'}`} style={{ animationDelay: '0.4s' }} />
                                        </div>
                                        <div className="flex justify-between text-[10px] text-foreground-tertiary">
                                            <span>Validation</span>
                                            <span>Sauvegarde</span>
                                            <span>Terminé</span>
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}

                        {modalMode === 'result' && validationResult && (
                            <PayPalKeyValidationStatus
                                isLoading={false}
                                result={validationResult}
                            />
                        )}
                    </DialogContent>

                    {modalMode === 'result' && (
                        <DialogActions className="flex justify-end py-4 px-6 border-t border-border bg-background-secondary">
                            <Button
                                onClick={() => setShowResultModal(false)}
                                variant="primary"
                                className="px-8"
                            >
                                Fermer
                            </Button>
                        </DialogActions>
                    )}
                </div>
            </TailwindDialog>
        </div>
    );
};

export default PayPalApiKeyConfig;
